import { useCallback, useMemo } from 'react';
import type { JoyHotelQueryStringParameters } from '@shared/components/DiscountsProvider/hotels/HotelsDiscountsProvider';
import { useFeatureFlagsContext } from '@shared/core/featureFlags';
import { useRetainSupportedStaysParams } from '@shared/hooks/useRetainSupportedStaysParams';
import type { UtmMedium } from '@shared/utils/utmParameters';
import { resolveAcquisitionAwareUtm, useUtmParameters } from '@shared/utils/utmParameters';
import { HotelSearchMapLayout, HotelSearchSource } from '@shared/constants/accommodations';

export type SearchParams = Readonly<{
  checkInDate?: string;
  checkOutDate?: string;
  name?: string;
  placeId?: string;
  skipAvailabilityCheck?: string;
  utm_campaign: string;
  utm_medium: UtmMedium;
  utm_source: string;
  /** Internal breadcrumb (e.g. `interceptor`); forwarded across hops, never minted by a route builder. */
  utm_source_secondary?: string;
  // Joy discount code for accommodations
  [JoyHotelQueryStringParameters.joyHotelDiscountCode]?: string;
  [JoyHotelQueryStringParameters.joyHotelDiscountRule]?: string;

  source?: HotelSearchSource;
  layout?: HotelSearchMapLayout;

  selectedHotel?: string;

  // Shared location/date query params (hotel + car search).
  destination?: string;
  lat?: string;
  lng?: string;
  googlePlaceId?: string;
  checkin?: string;
  checkout?: string;

  // hotel search route query params
  rooms?: string;
  radius?: string;

  // car search route query params.
  pickupLat?: string;
  pickupLng?: string;
  offerToken?: string;
  dropoffLat?: string;
  dropoffLng?: string;
  pickupTime?: string;
  dropoffTime?: string;
  pickupPlaceId?: string;
  dropoffPlaceId?: string;
  packagedProduct?: string;
  pickupDestination?: string;
  dropoffDestination?: string;
}>;

export type CreateSearchParamsForGuestMapParams = Readonly<{
  selectedHotel?: string;
  layout?: HotelSearchMapLayout;
  checkin?: string;
  checkout?: string;
}>;

export const createSearchParamsFromAccommodationsPage = (utmCampaign: string): SearchParams => {
  return { utm_medium: 'web', utm_source: 'website_accommodations_page', utm_campaign: utmCampaign };
};

export const createSearchParamsForGuestMap = (params?: CreateSearchParamsForGuestMapParams): SearchParams => {
  const { layout = HotelSearchMapLayout.FULL, selectedHotel, checkin, checkout } = params ?? {};

  return {
    layout,
    utm_medium: 'web',
    source: HotelSearchSource.GUEST_MAP,
    utm_campaign: HotelSearchSource.GUEST_MAP,
    utm_source: 'website_accommodations_page',
    ...(selectedHotel && { selectedHotel }),
    ...(checkin && { checkin }),
    ...(checkout && { checkout }),
  };
};

export const useHotelRoutePaths = () => {
  const featureFlagsContext = useFeatureFlagsContext();
  const supportedStaysParams = useRetainSupportedStaysParams();

  // Extract current URL UTM at hook level (hooks must be called at top level)
  const currentUtm = useUtmParameters();

  // Helper to merge UTM params and build URL
  const buildUrlWithPreservedUtm = useCallback(
    (baseUrl: string, searchParams: SearchParams | undefined) => {
      if (!searchParams) {
        return featureFlagsContext.formatToUrl(supportedStaysParams.retainSupportedStaysParams(baseUrl));
      }

      // Extract UTM from searchParams (discount codes are not UTM params)
      const internalUtm = {
        utm_medium: searchParams.utm_medium,
        utm_source: searchParams.utm_source,
        utm_campaign: searchParams.utm_campaign,
      };

      // Inbound UTM wins per slot, except an upstream internal `utm_source`, which yields to this CTA.
      const mergedUtm = resolveAcquisitionAwareUtm(currentUtm, internalUtm);

      // Merged searchParams with preserved UTM and discount codes. `utm_source_secondary` is forwarded
      // so an inbound breadcrumb survives internal hops.
      const mergedParams: SearchParams = {
        ...searchParams,
        ...(mergedUtm.utm_medium && { utm_medium: mergedUtm.utm_medium }),
        ...(mergedUtm.utm_source && { utm_source: mergedUtm.utm_source }),
        ...(mergedUtm.utm_campaign && { utm_campaign: mergedUtm.utm_campaign }),
        ...(mergedUtm.utm_source_secondary && { utm_source_secondary: mergedUtm.utm_source_secondary }),
      };

      // Filter out null/undefined/empty values to prevent "undefined" strings in URLs
      const filteredParams = Object.entries(mergedParams).reduce<Record<string, string>>((acc, [key, value]) => {
        if (value) {
          acc[key] = value;
        }
        return acc;
      }, {});
      const params = new URLSearchParams(filteredParams);
      const urlWithParams = `${baseUrl}?${params.toString()}`;
      return featureFlagsContext.formatToUrl(supportedStaysParams.retainSupportedStaysParams(urlWithParams));
    },
    [featureFlagsContext, supportedStaysParams, currentUtm],
  );

  return useMemo(
    () => ({
      hotelSearch: {
        path: '/:eventHandle/hotel/search',
        buildRelativeUrl: (eventHandle: string, searchParams: SearchParams) => {
          return buildUrlWithPreservedUtm(`/${eventHandle}/hotel/search`, searchParams);
        },
      },
      hotelDetails: {
        path: '/:eventHandle/hotel/:joyPlaceId?',
        buildRelativeUrl: (eventHandle: string, joyPlaceId: string, searchParams: SearchParams) => {
          return buildUrlWithPreservedUtm(`/${eventHandle}/hotel/${joyPlaceId}`, searchParams);
        },
      },
      carSearch: {
        path: '/:eventHandle/car/search',
        buildRelativeUrl: (eventHandle: string, searchParams?: SearchParams) => {
          return buildUrlWithPreservedUtm(`/${eventHandle}/car/search`, searchParams);
        },
      },
      carDetails: {
        path: '/:eventHandle/car/details',
      },
      bookYourStay: {
        path: '/:eventHandle/book-your-stay',
        buildRelativeUrl: (eventHandle: string, searchParams: SearchParams) => {
          return buildUrlWithPreservedUtm(`/${eventHandle}/book-your-stay`, searchParams);
        },
      },
      accommodationPlace: {
        path: '/:eventHandle/accommodation-place/:accommodationPlaceId?',
        buildRelativeUrl: (eventHandle: string, accommodationPlaceId: string, searchParams?: SearchParams) => {
          return buildUrlWithPreservedUtm(`/${eventHandle}/accommodation-place/${accommodationPlaceId}`, searchParams);
        },
      },
      accommodationsPage: {
        path: '/:eventHandle/accommodations',
        buildRelativeUrl: (eventHandle: string, searchParams?: SearchParams) => {
          return buildUrlWithPreservedUtm(`/${eventHandle}/accommodations`, searchParams);
        },
      },
      // Existing room block, hotelbooking and partner hotels routes
      roomblock: {
        path: `/:eventHandle/hotelbooking/roomblock/:id`,
        buildRelativeUrl: (eventHandle: string, id: string, searchParams?: SearchParams) => {
          return buildUrlWithPreservedUtm(`/${eventHandle}/hotelbooking/roomblock/${id}`, searchParams);
        },
      },
      hotelbooking: {
        path: `/:eventHandle/hotelbooking`,
        buildRelativeUrl: (eventHandle: string, searchParams?: SearchParams) => {
          return buildUrlWithPreservedUtm(`/${eventHandle}/hotelbooking`, searchParams);
        },
      },
      partnerhotels: {
        path: `/:eventHandle/hotelbooking/partnerhotels/:hotelId`,
        buildRelativeUrl: (eventHandle: string, hotelId: string, searchParams?: SearchParams) => {
          return buildUrlWithPreservedUtm(`/${eventHandle}/hotelbooking/partnerhotels/${hotelId}`, searchParams);
        },
      },
    }),
    [buildUrlWithPreservedUtm],
  );
};
